import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  Bell,
  Check,
  LayoutDashboard,
  LifeBuoy,
  MessageCircleQuestion,
  MessagesSquare,
} from 'lucide-react';
import { AssessmentBackdrop, HAIRLINE } from '../assessment/AssessmentUI';
import { portalService } from '../../services/api';
import { PortalNotification } from '../../types/portal';

const fa = (n: number) => n.toLocaleString('fa-IR');

const NAV = [
  { to: '/portal', label: 'داشبورد', icon: <LayoutDashboard className="h-4 w-4" /> },
  { to: '/portal/messages', label: 'گفتگو با منتور', icon: <MessagesSquare className="h-4 w-4" /> },
  { to: '/portal/questions', label: 'پرسش از منتور', icon: <MessageCircleQuestion className="h-4 w-4" /> },
  { to: '/portal/support', label: 'پشتیبانی', icon: <LifeBuoy className="h-4 w-4" /> },
];

/** فاصلهٔ زمانی خوانا — «۲ روز پیش» به‌جای تاریخ خام */
export function timeAgo(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const minutes = Math.round(diff / 60000);
  if (minutes < 1) return 'همین حالا';
  if (minutes < 60) return fa(minutes) + ' دقیقه پیش';
  const hours = Math.round(minutes / 60);
  if (hours < 24) return fa(hours) + ' ساعت پیش';
  const days = Math.round(hours / 24);
  if (days < 30) return fa(days) + ' روز پیش';
  return new Date(iso).toLocaleDateString('fa-IR');
}

/**
 * فاصله تا مهلت، با جهت.
 * عدد خام تاریخ به کاربر نمی‌گوید عجله کند یا نه؛ «۲ روز مانده» می‌گوید.
 */
export function dueLabel(iso?: string | null): { text: string; overdue: boolean; soon: boolean } | null {
  if (!iso) return null;
  const diff = new Date(iso).getTime() - Date.now();
  const days = Math.ceil(diff / 86400000);
  if (diff < 0) {
    const past = Math.abs(days);
    return { text: past <= 1 ? 'مهلت دیروز تمام شد' : fa(past) + ' روز از مهلت گذشته', overdue: true, soon: false };
  }
  if (days <= 1) return { text: 'مهلت امروز', overdue: false, soon: true };
  if (days <= 2) return { text: fa(days) + ' روز تا مهلت', overdue: false, soon: true };
  return { text: fa(days) + ' روز تا مهلت', overdue: false, soon: false };
}

/** زنگ اعلان با فهرست کشویی */
const NotificationBell: React.FC = () => {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState<PortalNotification[]>([]);
  const boxRef = useRef<HTMLDivElement>(null);

  const load = useCallback(async () => {
    try {
      setItems(await portalService.getNotifications());
    } catch {
      /* اعلان نباید صفحه را بشکند */
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  // بستن با کلیک بیرون — بدون این، فهرست روی موبایل گیر می‌کند
  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (boxRef.current && !boxRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [open]);

  const unread = items.filter((i) => !i.isRead).length;

  const markAll = async () => {
    await portalService.markNotificationsRead();
    setItems((prev) => prev.map((i) => ({ ...i, isRead: true })));
  };

  const openOne = async (item: PortalNotification) => {
    if (!item.isRead) {
      await portalService.markNotificationsRead(item.id);
      setItems((prev) => prev.map((i) => (i.id === item.id ? { ...i, isRead: true } : i)));
    }
  };

  return (
    <div ref={boxRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="relative flex h-9 w-9 items-center justify-center rounded-xl border bg-white text-slate-500 transition hover:text-[#1C3B9B]"
        style={{ borderColor: HAIRLINE }}
        aria-label="اعلان‌ها"
      >
        <Bell className="h-4 w-4" />
        {unread ? (
          <span className="absolute -right-1 -top-1 flex h-4 min-w-[16px] items-center justify-center rounded-full bg-[#d03b3b] px-1 text-[9px] font-black text-white">
            {fa(unread)}
          </span>
        ) : null}
      </button>

      {open ? (
        <div
          className="absolute left-0 z-40 mt-2 w-[min(340px,calc(100vw-2rem))] overflow-hidden rounded-2xl border bg-white shadow-[0_20px_50px_-20px_rgba(15,23,42,0.35)]"
          style={{ borderColor: HAIRLINE }}
        >
          <div
            className="flex items-center justify-between border-b px-3 py-2.5"
            style={{ borderColor: HAIRLINE }}
          >
            <p className="text-[12.5px] font-black text-slate-800">اعلان‌ها</p>
            {unread ? (
              <button
                type="button"
                onClick={markAll}
                className="flex items-center gap-1 text-[11px] font-black text-[#1C3B9B]"
              >
                <Check className="h-3 w-3" />
                خواندن همه
              </button>
            ) : null}
          </div>

          <div className="max-h-[60vh] overflow-y-auto">
            {items.length ? (
              items.map((item) => {
                const inner = (
                  <>
                    <span className="flex items-start gap-2">
                      {!item.isRead ? (
                        <span
                          aria-hidden
                          className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-[#1C3B9B]"
                        />
                      ) : (
                        <span aria-hidden className="mt-1.5 h-2 w-2 shrink-0" />
                      )}
                      <span className="min-w-0 flex-1">
                        <span
                          className={
                            'block text-[12px] leading-6 ' +
                            (item.isRead ? 'font-bold text-slate-500' : 'font-black text-slate-900')
                          }
                        >
                          {item.title}
                        </span>
                        {item.body ? (
                          <span className="mt-0.5 block text-[11px] leading-6 text-slate-400">
                            {item.body}
                          </span>
                        ) : null}
                        <span className="mt-0.5 block text-[10px] font-bold text-slate-300">
                          {timeAgo(item.createdAt)}
                        </span>
                      </span>
                    </span>
                  </>
                );

                return item.linkPath ? (
                  <Link
                    key={item.id}
                    to={item.linkPath}
                    onClick={() => {
                      openOne(item);
                      setOpen(false);
                    }}
                    className="block border-b px-3 py-2.5 transition hover:bg-slate-50"
                    style={{ borderColor: HAIRLINE }}
                  >
                    {inner}
                  </Link>
                ) : (
                  <button
                    key={item.id}
                    type="button"
                    onClick={() => openOne(item)}
                    className="block w-full border-b px-3 py-2.5 text-right transition hover:bg-slate-50"
                    style={{ borderColor: HAIRLINE }}
                  >
                    {inner}
                  </button>
                );
              })
            ) : (
              <p className="py-10 text-center text-[12px] font-bold text-slate-400">
                اعلانی ندارید.
              </p>
            )}
          </div>
        </div>
      ) : null}
    </div>
  );
};

interface ShellProps {
  children: React.ReactNode;
  /** برای صفحهٔ تسک که ناوبری خودش را دارد */
  compact?: boolean;
}

/**
 * قاب مشترک پرتال: ناوبری و زنگ اعلان.
 *
 * بدون این، اعلان‌ها ساخته می‌شدند ولی هیچ‌جا خوانده نمی‌شدند و کاربر برای
 * رفتن بین بخش‌ها مجبور بود از داشبورد سایت دور بزند.
 */
export const PortalShell: React.FC<ShellProps> = ({ children, compact }) => {
  const { pathname } = useLocation();

  return (
    <AssessmentBackdrop>
      <div className="mx-auto max-w-5xl px-4 pb-16 pt-4 sm:px-6">
        <div className="mb-4 flex items-center gap-2">
          <nav className="flex min-w-0 flex-1 items-center gap-1 overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
            {NAV.map((item) => {
              const active =
                item.to === '/portal' ? pathname === '/portal' : pathname.startsWith(item.to);
              return (
                <Link
                  key={item.to}
                  to={item.to}
                  className={
                    'flex shrink-0 items-center gap-1.5 rounded-xl border px-3 py-2 text-[12px] font-black transition ' +
                    (active
                      ? 'border-transparent bg-[#1C3B9B] text-white'
                      : 'bg-white text-slate-500 hover:text-[#1C3B9B]')
                  }
                  style={active ? undefined : { borderColor: HAIRLINE }}
                >
                  {item.icon}
                  {item.label}
                </Link>
              );
            })}
          </nav>
          <NotificationBell />
        </div>

        {compact ? null : null}
        {children}
      </div>
    </AssessmentBackdrop>
  );
};
